import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Table, Form, Offcanvas } from "react-bootstrap";
import {
  suspendSims,
  validateSimsForAction,
} from "../../../../redux/slices/sims";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import NotyfContext from "../../../../contexts/NotyfContext";
import * as Yup from "yup";
import { Formik } from "formik";
import Loader from "../../../../components/spinners/Loader";
import useWindowSize from "../../../../hooks/useWindowSize";
import useDeviceType from "../../../../hooks/useDeviceType";
import { useWhitelabel } from "../../../../contexts/WhitelabelContext";

const SuspendSimModal = ({
  sims,
  openSuspendModal,
  setOpenSuspendModal,
  clearSelectedRows,
}) => {
  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const dispatch = useDispatch();
  const handleClose = () => setOpenSuspendModal(false);
  const notyf = useContext(NotyfContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [internalShow, setInternalShow] = useState(openSuspendModal);
  const [fadeOut, setFadeOut] = useState(false);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [fileInputKey, setFileInputKey] = React.useState(Date.now());
  const { backgroundColor, fontColor } = useWhitelabel();

  useEffect(() => {
    if (openSuspendModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [openSuspendModal, isDesktop]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      setOpenSuspendModal(false);
    }, 300);
  };

  const initialValues = {
    selectedSims: sims.length > 0 ? sims : [],
    file: null,
  };

  const validation = Yup.object().shape({
    selectedSims: Yup.array().required("Please select at least one SIM"),
    file: Yup.mixed().when("$requireFile", {
      is: true,
      then: Yup.mixed()
        .required("File is required")
        .test("fileType", "Only CSV or XLSX files are accepted", (value) => {
          if (!value) return false;
          const allowedTypes = [
            "text/csv",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
          ];
          return allowedTypes.includes(value.type);
        }),
      otherwise: Yup.mixed().nullable().notRequired(),
    }),
  });

  if (isDesktop) {
    return (
      <Modal show={openSuspendModal} onHide={handleClose} backdrop="static">
        <Modal.Header>
          <Modal.Title>Suspend SIMs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            context={{ requireFile: sims.length === 0 }}
            onSubmit={async (values, { setStatus, setValues }) => {
              const data = {
                sims: values.selectedSims.map((sim) => sim.imsi),
              };
              await dispatch(suspendSims(data.sims))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "SIMs suspended successfully",
                    duration: 5000,
                    dismissible: false,
                    ripple: true,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Success");
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    dismissible: false,
                    ripple: true,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Error");
                })
                .finally(() => {
                  setCurrentStep(3);
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              status,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              setValues,
              validateForm,
              setFieldTouched,
            }) => {
              const nextStep = () => {
                validateForm().then((errors) => {
                  if (Object.keys(errors).length === 0) {
                    if (currentStep === 1) {
                      setCurrentStep(2);
                      handleSubmit();
                    }
                  } else {
                    Object.keys(values).forEach((field) => {
                      setFieldTouched(field);
                    });
                  }
                });
              };
              return (
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && values.selectedSims.length === 0 && (
                    <React.Fragment>
                      <Form.Group className="mb-3">
                        <Form.Label>Upload File</Form.Label>

                        <div className="custom-file-upload-wrapper">
                          <label
                            htmlFor="file-upload"
                            className="custom-file-upload-btn"
                            style={{
                              "--dynamic-font-color":
                                whitelabel?.fontColor ?? "#fff",
                              "--dynamic-bg-color":
                                whitelabel?.backgroundColor ?? "#0E1A2B",
                              border: "none",
                            }}
                          >
                            Choose File
                          </label>
                          <input
                            key={fileInputKey}
                            id="file-upload"
                            type="file"
                            name="file"
                            accept=".csv,.xlsx"
                            style={{ display: "none" }}
                            onChange={async (event) => {
                              setFieldValue(
                                "file",
                                event.currentTarget.files[0]
                              );
                              await validateSimsForAction({
                                file: event.currentTarget.files[0],
                                action: "suspend",
                              })
                                .then((response) => {
                                  notyf.open({
                                    type: "success",
                                    message: "Sim file validated successfully",
                                    duration: 5000,
                                    ripple: true,
                                    dismissible: false,
                                    position: {
                                      x: "center",
                                      y: "top",
                                    },
                                  });
                                  setFieldValue("selectedSims", response);
                                })
                                .catch((error) => {
                                  notyf.open({
                                    type: "error",
                                    message: error,
                                    duration: 5000,
                                    ripple: true,
                                    dismissible: false,
                                    position: {
                                      x: "center",
                                      y: "top",
                                    },
                                  });
                                  setFieldValue("file", null);
                                  setFileInputKey(new Date());
                                });
                            }}
                            onBlur={() => setFieldTouched("file", true)}
                          />
                          {values.file && (
                            <div className="mt-2">
                              <small className="text-muted">
                                Selected: {values.file.name}
                              </small>
                            </div>
                          )}
                          {touched.file && errors.file && (
                            <div className="text-danger mt-1">
                              {errors.file}
                            </div>
                          )}
                        </div>
                      </Form.Group>
                    </React.Fragment>
                  )}
                  {currentStep === 1 && values.selectedSims.length > 0 && (
                    <React.Fragment>
                      <div
                        style={{
                          marginBottom: "1rem",
                          padding: "0.75rem 1rem",
                          backgroundColor: backgroundColor,
                          borderRadius: "0.5rem",
                          fontSize: "0.9rem",
                          color: fontColor,
                        }}
                      >
                        <strong style={{ color: fontColor }}>
                          🛈 Important Information
                        </strong>
                        <br />
                        <br />
                        SIM Suspension{" "}
                        <strong>
                          temporarily disables the SIM card’s connectivity
                        </strong>
                        . The SIM card <strong>remains active</strong> on its
                        current plan, and subscription fees{" "}
                        <strong>continue to apply</strong> during the
                        suspension.
                        <br />
                        <br />
                        This configuration is <strong>temporary</strong> and
                        applies
                        <strong> only for the current billing period</strong>.
                        It will automatically reset to the default settings at
                        the start of the next billing cycle.
                      </div>
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>IMSI</th>
                            <th>ICCID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {values.selectedSims.map((item, index) => {
                            return (
                              <tr key={index}>
                                <td>{item.imsi}</td>
                                <td>{item.iccid}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </React.Fragment>
                  )}
                  {currentStep === 2 && <Loader />}
                  {currentStep === 3 && status === "Success" && (
                    <div className="text-center">
                      <FaCheckCircle
                        size={120}
                        className="mt-3"
                        color="#4BBF73"
                      />
                      <h3 className="mt-3">Order Complete</h3>
                    </div>
                  )}
                  {currentStep === 3 && status === "Error" && (
                    <div className="text-center">
                      <FaExclamationCircle
                        size={120}
                        className="mt-3"
                        color="#FF9494"
                      />
                      <h3 className="mt-3">Order Error</h3>
                      <p>Please contact the administrator.</p>
                    </div>
                  )}
                  <Modal.Footer>
                    {currentStep === 1 && (
                      <>
                        <Button
                          onClick={() => {
                            setValues(initialValues);
                            setOpenSuspendModal(false);
                          }}
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          onClick={nextStep}
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                          }}
                        >
                          Suspend
                        </Button>
                      </>
                    )}
                    {currentStep === 3 && (
                      <Button
                        onClick={() => {
                          setOpenSuspendModal(false);
                          setValues(initialValues);
                          setCurrentStep(1);
                          if (clearSelectedRows) {
                            clearSelectedRows();
                          }
                        }}
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#ffffff",
                          border: "none",
                        }}
                      >
                        Close
                      </Button>
                    )}
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  } else {
    return (
      <Offcanvas
        placement="bottom"
        show={internalShow}
        onHide={handleHide}
        scrollable="true"
        className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
      >
        <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
          <Offcanvas.Title>Suspend SIMs</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="fullscreen-offcanvas-body">
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setStatus, setValues }) => {
              const data = {
                sims: values.selectedSims.map((sim) => sim.imsi),
              };
              await dispatch(suspendSims(data.sims))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "SIMs suspended successfully",
                    duration: 5000,
                    dismissible: false,
                    ripple: true,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Success");
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    dismissible: false,
                    ripple: true,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Error");
                })
                .finally(() => {
                  setCurrentStep(3);
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              status,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              setValues,
              validateForm,
              setFieldTouched,
            }) => {
              const nextStep = () => {
                validateForm().then((errors) => {
                  if (Object.keys(errors).length === 0) {
                    if (currentStep === 1) {
                      setCurrentStep(2);
                      handleSubmit();
                    }
                  } else {
                    Object.keys(values).forEach((field) => {
                      setFieldTouched(field);
                    });
                  }
                });
              };
              return (
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && (
                    <Table responsive>
                      <thead>
                        <tr>
                          <th>IMSI</th>
                          <th>ICCID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.selectedSims.map((item, index) => {
                          return (
                            <tr key={index}>
                              <td>{item.imsi}</td>
                              <td>{item.iccid}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                  {currentStep === 2 && <Loader />}
                  {currentStep === 3 && status === "Success" && (
                    <div className="text-center">
                      <FaCheckCircle
                        size={80}
                        className="mt-3"
                        color="#4BBF73"
                      />
                      <h3 className="mt-3">Order Complete</h3>
                    </div>
                  )}
                  {currentStep === 3 && status === "Error" && (
                    <div className="text-center">
                      <FaExclamationCircle
                        size={80}
                        className="mt-3"
                        color="#FF9494"
                      />
                      <h3 className="mt-3">Order Error</h3>
                      <p>Please contact the administrator.</p>
                    </div>
                  )}
                  <div className="d-flex flex-column align-items-center mt-3">
                    {currentStep === 1 && (
                      <>
                        <Button
                          variant="primary"
                          onClick={nextStep}
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                          className="mb-3"
                        >
                          Suspend
                        </Button>
                        <Button
                          onClick={() => {
                            setValues(initialValues);
                            handleHide();
                          }}
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                            width: "100%",
                            padding: "10px",
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    {currentStep === 3 && (
                      <Button
                        onClick={() => {
                          handleHide();
                          setValues(initialValues);
                          setCurrentStep(1);
                          if (clearSelectedRows) {
                            clearSelectedRows();
                          }
                        }}
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#ffffff",
                          border: "none",
                          padding: "10px",
                          width: "100%",
                        }}
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Offcanvas.Body>
      </Offcanvas>
    );
  }
};

export default SuspendSimModal;
